/**
 * Attribute Filters - Custom filtering system for product metafields
 * Filtrerer produkter baseret på valgte metafield værdier (roast_level, processing, origin)
 */

class AttributeFilters {
  constructor() {
    this.activeFilters = new Set();
    this.productItems = [];
    this.allProducts = [];
    this.filterEntries = [];
    this.init();
  }

  init() {
    // Vent på at DOM er klar
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    const container = document.querySelector('.attribute-filters-container');
    if (!container) return;

    // Find alle produkt items i grid FØRST for at samle metafield værdier
    const resultsList = document.querySelector('results-list');
    if (resultsList) {
      this.productItems = Array.from(resultsList.querySelectorAll('.product-grid__item'));
    } else {
      // Fallback: find direkte i collection wrapper
      const collectionWrapper = document.querySelector('.collection-wrapper');
      if (collectionWrapper) {
        this.productItems = Array.from(collectionWrapper.querySelectorAll('.product-grid__item'));
      }
    }

    if (this.productItems.length === 0) {
      // Hvis ingen produkter, skjul container
      container.style.display = 'none';
      return;
    }

    // Saml alle unikke metafield-værdier pr. felt fra produkterne
    const allFiltersMap = new Map();
    this.allProducts = this.productItems.map(item => {
      const productId = item.dataset.productId;
      
      // Hent metafield værdier direkte fra list item's data attributes
      const metafields = this.getProductMetafields(item);
      
      // Tilføj værdier til map for at samle unikke felt+værdi kombinationer
      Object.entries(metafields).forEach(([field, value]) => {
        if (value && value.trim() !== '') {
          const valueTrimmed = value.trim();
          const filterKey = this.buildFilterKey(field, valueTrimmed);
          if (!allFiltersMap.has(filterKey)) {
            allFiltersMap.set(filterKey, { field, value: valueTrimmed, key: filterKey });
          }
        }
      });
      
      return {
        element: item,
        id: productId,
        metafields: metafields
      };
    });

    // Hent ekskluderede værdier fra block settings (hvis tilgængelig)
    const excludedValuesString = container.dataset.excludedValues || '';
    const excludedValues = excludedValuesString ? excludedValuesString.split(',').map(v => v.trim().toLowerCase()) : [];
    
    // Filtrer ekskluderede værdier fra
    const filteredEntries = Array.from(allFiltersMap.values()).filter(entry => {
      const valueLower = entry.value.toLowerCase().trim();
      return !excludedValues.includes(valueLower);
    });

    // Hvis der ikke er nogen værdier efter filtrering, skjul container
    if (filteredEntries.length === 0) {
      container.style.display = 'none';
      return;
    }

    // Sorter først på værdi, derefter felt
    this.filterEntries = filteredEntries.sort((a, b) => {
      const valueCompare = a.value.localeCompare(b.value, undefined, { sensitivity: 'base' });
      if (valueCompare !== 0) return valueCompare;
      return this.getFieldLabel(a.field).localeCompare(this.getFieldLabel(b.field), undefined, { sensitivity: 'base' });
    });

    // Opdater filter knapper hvis de allerede eksisterer, ellers opret dem
    this.updateFilterButtons(this.filterEntries);

    // Find alle filter knapper
    this.filterButtons = container.querySelectorAll('.attribute-filter-button');
    if (this.filterButtons.length === 0) return;

    // Læs aktive værdier fra URL
    this.readActiveValuesFromURL();

    // Tilføj event listeners til knapper
    this.filterButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleFilterClick(e, button));
    });

    // Filtrer produkter baseret på initiale værdier
    this.filterProducts();
  }

  updateFilterButtons(entries) {
    const container = document.querySelector('.attribute-filters-container');
    if (!container) return;

    const wrapper = container.querySelector('.attribute-filters-wrapper');
    if (!wrapper) return;

    // Hvis der allerede er knapper, opdater dem
    const existingList = wrapper.querySelector('.attribute-filters-list');
    if (existingList) {
      // Opdater eksisterende liste
      existingList.innerHTML = entries.map(({ field, value, key }) => `
        <button
          type="button"
          class="attribute-filter-button"
          data-value="${this.escapeHtml(key)}"
          data-field="${this.escapeHtml(field)}"
          role="listitem"
          aria-label="${this.escapeHtml(value)} (${this.escapeHtml(this.getFieldLabel(field))})"
        >
          <span class="attribute-filter-button__text">${value.toUpperCase()}</span>
        </button>
      `).join('');
    } else {
      // Opret ny liste hvis den ikke eksisterer
      wrapper.innerHTML = `
        <div class="attribute-filters-list" role="list">
          ${entries.map(({ field, value, key }) => `
            <button
              type="button"
              class="attribute-filter-button"
              data-value="${this.escapeHtml(key)}"
              data-field="${this.escapeHtml(field)}"
              role="listitem"
              aria-label="${this.escapeHtml(value)} (${this.escapeHtml(this.getFieldLabel(field))})"
            >
              <span class="attribute-filter-button__text">${value.toUpperCase()}</span>
            </button>
          `).join('')}
        </div>
      `;
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  buildFilterKey(field, value) {
    return `${field}:${value}`;
  }

  parseFilterKey(filterKey) {
    const separatorIndex = filterKey.indexOf(':');
    if (separatorIndex === -1) {
      return { field: null, value: filterKey };
    }

    return {
      field: filterKey.slice(0, separatorIndex),
      value: filterKey.slice(separatorIndex + 1)
    };
  }

  getFieldLabel(field) {
    const labels = {
      roast_level: 'Roast level',
      processing: 'Processing',
      origin: 'Origin'
    };

    return labels[field] || field;
  }

  getProductMetafields(listItem) {
    const metafields = {};

    if (listItem.dataset.roastLevel && listItem.dataset.roastLevel.trim() !== '') {
      metafields.roast_level = listItem.dataset.roastLevel.trim();
    }

    if (listItem.dataset.processing && listItem.dataset.processing.trim() !== '') {
      metafields.processing = listItem.dataset.processing.trim();
    }

    if (listItem.dataset.origin && listItem.dataset.origin.trim() !== '') {
      metafields.origin = listItem.dataset.origin.trim();
    }

    return metafields;
  }

  readActiveValuesFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const valuesParam = urlParams.get('attributes');
    
    if (valuesParam) {
      const values = valuesParam.split(',').map(v => decodeURIComponent(v.trim())).filter(Boolean);
      const legacyValues = [];

      values.forEach(filterKey => {
        if (filterKey.includes(':')) {
          this.activeFilters.add(filterKey);
        } else {
          legacyValues.push(filterKey);
        }
      });

      // Backwards compatibility: gammel URL-struktur uden feltnavn
      if (legacyValues.length > 0 && this.filterEntries.length > 0) {
        legacyValues.forEach(legacyValue => {
          this.filterEntries.forEach(entry => {
            if (entry.value === legacyValue) {
              this.activeFilters.add(entry.key);
            }
          });
        });
      }
    }
  }

  handleFilterClick(event, button) {
    event.preventDefault();
    const filterKey = button.dataset.value;
    
    if (!filterKey) return;

    // Toggle værdi
    if (this.activeFilters.has(filterKey)) {
      this.activeFilters.delete(filterKey);
      button.classList.remove('attribute-filter-button--active');
    } else {
      this.activeFilters.add(filterKey);
      button.classList.add('attribute-filter-button--active');
    }

    // Opdater URL
    this.updateURL();

    // Filtrer produkter
    this.filterProducts();
  }

  updateURL() {
    const url = new URL(window.location.href);
    
    if (this.activeFilters.size > 0) {
      const valuesArray = Array.from(this.activeFilters).map(value => encodeURIComponent(value));
      url.searchParams.set('attributes', valuesArray.join(','));
    } else {
      url.searchParams.delete('attributes');
    }

    // Opdater URL uden at reload siden
    window.history.pushState({}, '', url);
  }

  filterProducts() {
    if (this.activeFilters.size === 0) {
      // Vis alle produkter hvis ingen værdier er valgt
      this.productItems.forEach(item => {
        item.style.display = '';
        item.removeAttribute('hidden');
      });
      return;
    }

    // Filtrer produkter - et produkt skal have MINDST ÉN af de valgte værdier
    this.productItems.forEach(item => {
      const productId = item.dataset.productId;
      const product = this.allProducts.find(p => p.id === productId);
      
      if (!product) {
        item.style.display = 'none';
        item.setAttribute('hidden', '');
        return;
      }

      // Tjek om produktet har mindst én af de valgte felt+værdi kombinationer
      const hasMatchingValue = Array.from(this.activeFilters).some(filterKey => {
        const { field, value } = this.parseFilterKey(filterKey);
        if (!field || !value) return false;
        return product.metafields[field] === value;
      });

      if (hasMatchingValue) {
        item.style.display = '';
        item.removeAttribute('hidden');
      } else {
        item.style.display = 'none';
        item.setAttribute('hidden', '');
      }
    });

    // Opdater aktive knapper baseret på URL
    this.updateActiveButtons();

    // Dispatch custom event for at andre scripts kan reagere
    const activeFilterArray = Array.from(this.activeFilters);
    const activeValueArray = Array.from(new Set(activeFilterArray.map(filterKey => this.parseFilterKey(filterKey).value).filter(Boolean)));

    const event = new CustomEvent('attributeFiltersChanged', {
      detail: {
        activeFilters: activeFilterArray,
        activeValues: activeValueArray,
        visibleCount: this.productItems.filter(item => !item.hasAttribute('hidden')).length
      }
    });
    document.dispatchEvent(event);
  }

  updateActiveButtons() {
    this.filterButtons.forEach(button => {
      const filterKey = button.dataset.value;
      if (this.activeFilters.has(filterKey)) {
        button.classList.add('attribute-filter-button--active');
      } else {
        button.classList.remove('attribute-filter-button--active');
      }
    });
  }
}

// Initialiser når DOM er klar
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AttributeFilters();
  });
} else {
  new AttributeFilters();
}

// Re-initialiser hvis der er AJAX navigation (f.eks. Shopify's native navigation)
document.addEventListener('shopify:section:load', () => {
  new AttributeFilters();
});
